import React from 'react';
import { Card } from './ui/card';
import { Heart, Check, Lock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Health benefits timeline anchored to days on quitting journey
const BENEFITS = [
  { days: 0.014, key: 'twenty_min', fr: '20 min : tension et rythme cardiaque se normalisent', en: '20 min: blood pressure & heart rate normalize' },
  { days: 0.333, key: 'eight_hours', fr: '8h : taux de monoxyde de carbone divisé par 2', en: '8h: carbon monoxide level halved' },
  { days: 1, key: 'one_day', fr: '24h : risque d\'infarctus commence à diminuer', en: '24h: heart attack risk begins to drop' },
  { days: 2, key: 'two_days', fr: '48h : goût et odorat s\'améliorent', en: '48h: taste and smell improve' },
  { days: 3, key: 'three_days', fr: '72h : respiration plus facile, bronches se relâchent', en: '72h: breathing easier, bronchi relax' },
  { days: 14, key: 'two_weeks', fr: '2 semaines : circulation sanguine améliorée', en: '2 weeks: blood circulation improves' },
  { days: 30, key: 'one_month', fr: '1 mois : énergie en hausse, toux réduite', en: '1 month: energy up, cough reduced' },
  { days: 90, key: 'three_months', fr: '3 mois : fonction pulmonaire +30%', en: '3 months: lung function +30%' },
  { days: 365, key: 'one_year', fr: '1 an : risque de maladie cardiaque divisé par 2', en: '1 year: heart disease risk halved' },
  { days: 1825, key: 'five_years', fr: '5 ans : risque d\'AVC = non-fumeur', en: '5 years: stroke risk = non-smoker' },
  { days: 3650, key: 'ten_years', fr: '10 ans : risque de cancer du poumon divisé par 2', en: '10 years: lung cancer risk halved' },
];

export const HealthTimelineCard = ({ daysOnJourney = 0 }) => {
  const { language } = useLanguage();
  const nextUnlocked = BENEFITS.find((b) => daysOnJourney < b.days);
  const unlockedCount = BENEFITS.filter((b) => daysOnJourney >= b.days).length;

  return (
    <Card
      className="rounded-3xl border-0 shadow-lg p-6 animate-fade-in-up bg-white"
      data-testid="health-timeline-card"
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-rose-100 to-pink-100 flex items-center justify-center">
          <Heart className="w-6 h-6 text-rose-600" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900">
            {language === 'fr' ? 'Bénéfices santé' : 'Health benefits'}
          </h3>
          <p className="text-xs text-gray-500">
            {unlockedCount} / {BENEFITS.length} {language === 'fr' ? 'débloqués' : 'unlocked'}
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-5">
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-rose-400 to-pink-500 transition-all duration-1000"
            style={{ width: `${(unlockedCount / BENEFITS.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Next milestone */}
      {nextUnlocked && (
        <div className="p-3 rounded-2xl bg-amber-50 border border-amber-200 mb-4" data-testid="next-milestone">
          <p className="text-[10px] uppercase tracking-wider text-amber-700 font-bold mb-1">
            {language === 'fr' ? 'Prochain palier' : 'Next milestone'}
          </p>
          <p className="text-sm text-amber-900 font-medium">
            {language === 'fr' ? nextUnlocked.fr : nextUnlocked.en}
          </p>
          <p className="text-xs text-amber-700 mt-1">
            {language === 'fr' ? 'Encore' : 'In'}{' '}
            <span className="font-bold">
              {Math.ceil(nextUnlocked.days - daysOnJourney)} {language === 'fr' ? 'jour(s)' : 'day(s)'}
            </span>
          </p>
        </div>
      )}

      {/* Timeline list (scrollable) */}
      <div className="space-y-2 max-h-60 overflow-y-auto pr-2 custom-scroll">
        {BENEFITS.map((b) => {
          const unlocked = daysOnJourney >= b.days;
          return (
            <div
              key={b.key}
              className={`flex items-start gap-2.5 p-2 rounded-xl transition-colors ${unlocked ? 'bg-green-50' : 'bg-gray-50/60'}`}
              data-testid={`benefit-${b.key}`}
            >
              <div
                className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center ${unlocked ? 'bg-green-500' : 'bg-gray-300'}`}
              >
                {unlocked ? (
                  <Check className="w-3.5 h-3.5 text-white" />
                ) : (
                  <Lock className="w-3 h-3 text-white" />
                )}
              </div>
              <p className={`text-xs ${unlocked ? 'text-green-900 font-medium' : 'text-gray-500'}`}>
                {language === 'fr' ? b.fr : b.en}
              </p>
            </div>
          );
        })}
      </div>
    </Card>
  );
};
